import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Badge, Search, Kpi, Block, eur, num, fdate, inputCls, btnGold } from './ui';
import { useConfirm } from '../ConfirmDialog';

function BrickForm({ onDone }) {
  const [f, setF] = useState({ holder_id: '', bricks: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await dashboardApi.cdmAddBricks({ holder_id: f.holder_id, bricks: Number(f.bricks), reason: f.reason });
      toast.success('Ajuste de bricks guardado');
      setF({ holder_id: '', bricks: '', reason: '' });
      onDone();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="brick-form">
      <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold block">Socio o broker (ID)</span>
        <input required value={f.holder_id} onChange={(e) => setF({ ...f, holder_id: e.target.value.toUpperCase() })} className={`${inputCls} w-32`} placeholder="B42" /></label>
      <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold block">Bricks (+ añade, − quita)</span>
        <input required type="number" step="0.1" value={f.bricks} onChange={(e) => setF({ ...f, bricks: e.target.value })} className={`${inputCls} w-40`} placeholder="50 o -20" /></label>
      <label className="block flex-1 min-w-[200px]"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold block">Motivo</span>
        <input required minLength={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${inputCls} w-full`} placeholder="Ej. bonificación por campaña de octubre" /></label>
      <button disabled={busy} className={btnGold}>Guardar ajuste</button>
    </form>
  );
}

export default function CdmFhct() {
  const [dialog, ask] = useConfirm();
  const { data, error, reload } = useLoad(() => dashboardApi.cdmFhct(), []);
  const [q, setQ] = useState('');
  const [fund, setFund] = useState('todos');
  const rows = useMemo(() => (data?.purchases || []).filter((p) => (fund === 'todos' || p.funding === fund)
    && (!q || [p.id, p.member_id, p.member_name, p.n1].some((v) => (v || '').toLowerCase().includes(q.toLowerCase())))), [data, q, fund]);
  if (!data) return <Loading error={error} />;
  const labels = data.funding_labels;
  const t = data.totals;
  const setFunding = async (p, funding) => {
    try { await dashboardApi.cdmPatchFhct(p.id, { funding }); toast.success('Origen actualizado'); reload(); } catch (e) { toast.error(e.message); }
  };
  const delAdj = async (a) => {
    const ok = await ask({
      title: `Eliminar el ajuste de bricks de ${a.holder_id}`,
      details: [
        { label: 'Bricks', value: `${a.bricks > 0 ? '+' : ''}${num(a.bricks, 1)}` },
        { label: 'Motivo', value: a.reason || '—' },
        'Su saldo de bricks se recalcula sin este ajuste. Queda constancia en el historial de cambios.',
      ],
      confirmLabel: 'Eliminar ajuste',
    });
    if (!ok) return;
    try { await dashboardApi.cdmDeleteBricks(a.id); toast.success('Ajuste eliminado'); reload(); } catch (e) { toast.error(e.message); }
  };
  const cols = [
    { key: 'id', label: 'Compra', className: 'text-white font-medium', render: (p) => (<div>{p.id}<p className="text-xs text-gray-400">{fdate(p.date)}</p></div>) },
    { key: 'member_id', label: 'Socio', render: (p) => (<div>{p.member_id || <Badge tone="warn">Sin ID</Badge>}<p className="text-xs text-gray-400">{p.member_name}</p></div>) },
    { key: 'units', label: 'FHCT', align: 'right', render: (p) => (p.units ? num(p.units) : '—') },
    { key: 'contribution_eur', label: 'Aportación', align: 'right', render: (p) => eur(p.contribution_eur) },
    { key: 'funding', label: 'Cómo se pagó', render: (p) => (
      <select value={p.funding} onChange={(e) => setFunding(p, e.target.value)} aria-label={`Origen de ${p.id}`} className={`${inputCls} py-1 text-xs`}>
        {Object.entries(labels).map(([k, v]) => <option key={k} value={k} className="bg-[#0d1829]">{v}</option>)}
      </select>) },
    { key: 'n1', label: 'Brokers', render: (p) => [p.n1, p.n2, p.n3].filter(Boolean).join(' › ') || '—' },
    { key: 'status', label: 'Estado', render: (p) => (<div className="flex flex-wrap gap-1"><Badge tone={p.in_portfolio ? 'good' : 'bad'}>{p.in_portfolio ? 'En cartera' : 'Fuera de cartera'}</Badge><Badge>{p.status}</Badge></div>) },
    { key: 'bricks_total', label: 'Bricks', align: 'right', render: (p) => (p.generates_bricks ? num(p.bricks_total, 1) : <span title="Compra anterior al corte de bricks">—</span>) },
    { key: 'commission_eur', label: 'Comisión', align: 'right', render: (p) => eur(p.commission_eur) },
  ];
  return (
    <div className="space-y-6" data-testid="cdm-fhct">
      {dialog}
      <Block title="Cartera de FHCT por origen del pago">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(labels).map(([k, v]) => {
            const b = t.by_funding[k] || { units: 0, units_active: 0, eur_active: 0, count: 0 };
            return <button key={k} onClick={() => setFund(fund === k ? 'todos' : k)} className="text-left"><Kpi label={v} value={num(b.units_active)} sub={`${b.count} compras · ${eur(b.eur_active)}${b.units > b.units_active ? ` · ${num(b.units - b.units_active)} fuera de cartera` : ''}`} tone={fund === k ? 'good' : undefined} /></button>;
          })}
        </div>
        <p className="text-xs text-gray-400">Solo cuenta en cartera lo cobrado y no retirado (decisión 07/10/2026). «Sin cobro propio» queda fuera. Pulsa una tarjeta para filtrar.</p>
      </Block>
      <Block title="Bricks">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Vigentes" value={num(t.bricks_active, 1)} />
          <Kpi label="De socios" value={num(t.bricks_members, 1)} />
          <Kpi label="De brokers" value={num(t.bricks_brokers, 1)} />
          <Kpi label="Ajustes manuales" value={num(t.bricks_manual, 1)} />
          <Kpi label="Revocados" value={num(t.bricks_revoked, 1)} />
        </div>
        <BrickForm onDone={reload} />
        {data.adjustments.length > 0 && (
          <Table rowKey="id" rows={data.adjustments} cols={[
            { key: 'date', label: 'Fecha', render: (a) => fdate(a.date) },
            { key: 'holder_id', label: 'ID', className: 'text-white' },
            { key: 'bricks', label: 'Bricks', align: 'right', render: (a) => <span className={a.bricks < 0 ? 'text-red-300' : 'text-emerald-300'}>{a.bricks > 0 ? '+' : ''}{num(a.bricks, 1)}</span> },
            { key: 'reason', label: 'Motivo' },
            { key: 'by', label: 'Por' },
            { key: 'x', label: '', noSort: true, render: (a) => <button onClick={() => delAdj(a)} aria-label="Eliminar ajuste" className="text-gray-400 hover:text-red-300"><Trash2 size={14} /></button> },
          ]} />
        )}
      </Block>
      <Block title={`Compras de FHCT (${rows.length})`} right={<Search value={q} onChange={setQ} placeholder="Buscar compra, socio o broker" />}>
        <Table cols={cols} rows={rows} empty="Sin compras con este filtro" />
      </Block>
    </div>
  );
}
